
import { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { useUser } from '@/contexts/UserContext';
import { OpsEndpointCard } from '@/components/ops/OpsEndpointCard';
import { TimestampDisplay } from '@/components/ops/TimestampDisplay';
import { EmptyState } from '@/components/ops/EmptyState';
import { LoadingSpinner } from '@/components/ops/LoadingSpinner';
import { HealthIndicator } from '@/components/ops/HealthIndicator';
import { OpsErrorBoundary } from '@/components/ops/OpsErrorBoundary';
import { AlertTriangle, TrendingDown } from 'lucide-react';

const API_BASE = import.meta.env.VITE_RAG_API_URL;

interface DegradationState {
    reranker_health: 'healthy' | 'degraded' | 'unknown';
    recent_fallbacks: number;
    last_fallback_at: string | null;
    lookback_minutes: number;
    timestamp: string;
}

export function DegradationPage() {
    const { session } = useUser();
    const [data, setData] = useState<DegradationState | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [lookback, setLookback] = useState<string>("15");
    const isMounted = useRef(true);

    useEffect(() => {
        isMounted.current = true;
        if (session?.access_token) {
            fetchData();
        } else {
            setLoading(false);
            setError("Authentication required");
        }
        return () => {
            isMounted.current = false;
        };
    }, [session, lookback]);

    const fetchData = async () => {
        if (!session?.access_token) return;

        try {
            setLoading(true);
            setError(null);
            const res = await axios.get(`${API_BASE}/ops/retrieval/degradation_state`, {
                params: { lookback_minutes: lookback },
                headers: { Authorization: `Bearer ${session.access_token}` },
                timeout: 5000
            });

            if (isMounted.current) {
                setData(res.data);
            }
        } catch (err: any) {
            console.error("DEGRADATION FETCH ERROR BODY:", JSON.stringify(err.response?.data, null, 2));
            console.error("DEGRADATION FETCH STATUS:", err.response?.status);
            if (isMounted.current) {
                if (err.response?.status === 401) {
                    setError("Unauthorized");
                } else {
                    setError(err.message || "Failed to fetch degradation state");
                }
            }
        } finally {
            if (isMounted.current) {
                setLoading(false);
            }
        }
    };

    if (!session) return <EmptyState title="Authentication Required" description="Please sign in to view degradation status." />;

    return (
        <OpsErrorBoundary>
            <div className="h-[calc(100vh-12rem)] max-w-4xl mx-auto p-4">
                <OpsEndpointCard
                    title="Reranker Degradation Status"
                    endpoint="/ops/retrieval/degradation_state"
                    lastUpdated={data ? new Date(data.timestamp).getTime() : undefined}
                    onRefresh={fetchData}
                    isLoading={loading}
                    error={error}
                >
                    <div className="mb-6 flex justify-end items-center gap-2">
                        <span className="text-sm text-muted-foreground">Lookback Period:</span>
                        <select
                            className="h-9 w-[120px] rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                            value={lookback}
                            onChange={(e) => setLookback(e.target.value)}
                            style={{ backgroundColor: '#0A0A0A', color: 'white' }}
                        >
                            <option value="15">15 Minutes</option>
                            <option value="30">30 Minutes</option>
                            <option value="60">1 Hour</option>
                            <option value="1440">24 Hours</option>
                        </select>
                    </div>

                    {loading ? (
                        <LoadingSpinner fullHeight text="Analyzing failure patterns..." />
                    ) : data ? (
                        <div className="flex flex-col gap-8">

                            <div className="flex items-center justify-between bg-muted/10 p-6 rounded-lg border">
                                <div className="flex items-center gap-4">
                                    <div className="p-3 bg-muted rounded-full">
                                        <TrendingDown className="h-6 w-6 text-primary" />
                                    </div>
                                    <div>
                                        <h3 className="text-lg font-medium">Reranker Health</h3>
                                        <p className="text-sm text-muted-foreground">Status over last {data.lookback_minutes} minutes</p>
                                    </div>
                                </div>
                                <HealthIndicator status={data.reranker_health} />
                            </div>

                            <div className="grid grid-cols-1 gap-6">
                                <div className="p-6 border rounded-lg bg-card flex flex-col gap-2">
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm text-muted-foreground">Fallback Events Detected</span>
                                        {data.recent_fallbacks > 0 && <AlertTriangle className="h-4 w-4 text-yellow-500" />}
                                    </div>
                                    <div className="text-4xl font-bold mt-2">{data.recent_fallbacks}</div>
                                    {data.recent_fallbacks > 0 && (
                                        <p className="text-xs text-muted-foreground mt-2">
                                            The system bypassed reranking {data.recent_fallbacks} times due to failures or timeouts.
                                        </p>
                                    )}
                                </div>
                            </div>

                            <div className="p-6 border rounded-lg bg-card flex flex-col gap-2">
                                <h3 className="text-sm font-medium text-muted-foreground">Last Fallback Event</h3>
                                {data.last_fallback_at ? (
                                    <TimestampDisplay timestamp={data.last_fallback_at} label="Occurred at" />
                                ) : (
                                    <div className="flex items-center gap-2 text-green-500">
                                        <span className="text-sm">No fallbacks recorded in this window</span>
                                    </div>
                                )}
                            </div>

                        </div>
                    ) : (
                        <EmptyState title="No Status Available" description="Unable to determine degradation state." />
                    )}
                </OpsEndpointCard>
            </div>
        </OpsErrorBoundary>
    );
}
